/**
 * Desktop Mega Navigation Controller
 * 
 * Handles hover-based dropdown menus with proper timing to prevent flickering.
 * 
 * Key behaviors:
 * 1. Open dropdown immediately on hover
 * 2. Close dropdown with 150ms delay (allows mouse to reach dropdown)
 * 3. Cancel close if mouse enters dropdown before delay completes
 * 4. Smooth transitions between different dropdowns
 * 5. ESC key closes dropdown
 * 6. Click outside (overlay) closes dropdown
 * 7. Resize below 980px closes dropdown
 * 
 * Data attributes used:
 * - [data-mega-nav] - Main nav container
 * - [data-mega-nav-item] - Individual nav item (li)
 * - [data-has-dropdown="true"] - Nav items with dropdowns
 * - [data-mega-nav-dropdown] - Dropdown container
 * - [data-mega-nav-overlay] - Background overlay
 */

class MegaNavController {
  constructor(navElement) {
    // Store references
    this.nav = navElement;
    this.sectionId = navElement.dataset.sectionId;
    this.overlay = navElement.querySelector('[data-mega-nav-overlay]');
    
    // State
    this.activeItem = null;
    this.closeTimeout = null;
    this.isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    this.scrollHandler = null; // For scroll detection (fade indicator)
    
    // Sticky state
    this.isStickyEnabled = navElement.dataset.enableStickyDesktop === 'true';
    this.stickyThreshold = 100; // Same as main header
    this.stickyScrollHandler = null;
    this.isSticky = false;
    
    // Scroll direction detection (like main header)
    this.lastScrollY = window.scrollY;
    this.scrollThreshold = 10;
    this.scrollTicking = false;
    this.scrollDirectionListener = null;
    this.navHidden = false;
    this.headerObserver = null; // MutationObserver for header class changes
    
    // Configuration
    this.CLOSE_DELAY = 150; // ms - time to move mouse from nav item to dropdown
    this.MOBILE_BREAKPOINT = 980; // px - nav hidden below this
    
    // Bind methods to preserve context
    this.handleItemEnter = this.handleItemEnter.bind(this);
    this.handleItemLeave = this.handleItemLeave.bind(this);
    this.handleDropdownEnter = this.handleDropdownEnter.bind(this);
    this.handleDropdownLeave = this.handleDropdownLeave.bind(this);
    this.handleOverlayClick = this.handleOverlayClick.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    
    // Initialize
    this.init();
  }
  
  /**
   * Initialize event listeners
   */
  init() {
    // Get all nav items with dropdowns
    const dropdownItems = this.nav.querySelectorAll('[data-mega-nav-item][data-has-dropdown="true"]');
    
    dropdownItems.forEach(item => {
      const dropdown = item.querySelector('[data-mega-nav-dropdown]');
      
      if (!dropdown) return;
      
      // Desktop hover events
      if (!this.isTouch) {
        item.addEventListener('mouseenter', (e) => this.handleItemEnter(e, item, dropdown));
        item.addEventListener('mouseleave', (e) => this.handleItemLeave(e, item, dropdown));
        dropdown.addEventListener('mouseenter', (e) => this.handleDropdownEnter(e, item, dropdown));
        dropdown.addEventListener('mouseleave', (e) => this.handleDropdownLeave(e, item, dropdown));
      }
      
      // Touch device handling (tablets in landscape)
      if (this.isTouch) {
        item.addEventListener('touchstart', (e) => this.handleTouchStart(e, item, dropdown), { passive: true });
        
        // Click handler for touch - first tap opens, second tap navigates
        const link = item.querySelector('.mega-nav__link');
        if (link) {
          link.addEventListener('click', (e) => {
            if (this.activeItem !== item) {
              e.preventDefault();
              this.openDropdown(item, dropdown);
            }
            // If already active, allow navigation
          });
        }
      }
    });
    
    // Overlay click closes dropdown
    if (this.overlay) {
      this.overlay.addEventListener('click', this.handleOverlayClick);
    }
    
    // ESC key closes dropdown
    document.addEventListener('keydown', this.handleKeyDown);
    
    // Window resize handling
    this.debouncedResize = this.debounce(this.handleResize, 100);
    window.addEventListener('resize', this.debouncedResize);
    
    // Shopify section events
    document.addEventListener('shopify:section:unload', (e) => {
      if (e.detail.sectionId === this.sectionId) {
        this.destroy();
      }
    });
    
    // Setup sticky functionality if enabled
    if (this.isStickyEnabled) {
      this.setupStickyNav();
      this.setupScrollDirectionHandler();
    }
  }
  
  /**
   * Setup scroll direction handler (synchronized with main header)
   * Hides nav when scrolling down, shows when scrolling up
   * Directly syncs with header's state for perfect synchronization
   */
  setupScrollDirectionHandler() {
    if (!this.isStickyEnabled) return;
    
    // Find header reference for synchronization
    this.header = document.querySelector('.jrs-main-header');
    
    // Get header section wrapper for height calculation
    this.headerSection = this.header?.closest('#shopify-section-jrs-main-header') || 
                        document.querySelector('#shopify-section-jrs-main-header');
    
    this.scrollDirectionListener = () => {
      if (!this.scrollTicking) {
        requestAnimationFrame(() => {
          this.handleScrollDirection();
          this.scrollTicking = false;
        });
        this.scrollTicking = true;
      }
    };
    window.addEventListener('scroll', this.scrollDirectionListener, { passive: true });
    
    // Also listen for header state changes via custom events for instant sync
    if (this.header) {
      // Ensure section wrapper is available for CSS variable updates
      if (!this.sectionWrapper) {
        this.sectionWrapper = this.nav.closest('.shopify-section-desktop-mega-nav') || 
                             document.querySelector(`#shopify-section-${this.sectionId}`);
      }
      
      // Observe header for class changes using MutationObserver
      this.headerObserver = new MutationObserver(() => {
        // When header class changes, immediately sync nav - always match header state exactly
        const headerIsHidden = this.header.classList.contains('header-hidden');
        // Update CSS variable first (synchronously)
        this.updateHeaderHeightVariable(headerIsHidden);
        // Sync nav state immediately (synchronously) to prevent gaps
        this.toggleNavHidden(headerIsHidden);
      });
      
      this.headerObserver.observe(this.header, {
        attributes: true,
        attributeFilter: ['class']
      });
      
      // Initial sync - check header state immediately and always sync exactly
      const initialHeaderIsHidden = this.header.classList.contains('header-hidden');
      this.updateHeaderHeightVariable(initialHeaderIsHidden);
      this.toggleNavHidden(initialHeaderIsHidden);
    }
  }
  
  /**
   * Update CSS variable for header height when hidden
   * This allows the nav to move up by combined height when both are hidden
   */
  updateHeaderHeightVariable(headerIsHidden) {
    if (headerIsHidden && this.headerSection) {
      // Calculate header section wrapper height (the space it occupies in layout)
      // Force a reflow to get accurate measurements
      void this.headerSection.offsetHeight;
      const headerSectionHeight = this.headerSection.offsetHeight;
      
      // Set CSS variable on the nav section wrapper for use in CSS
      if (this.sectionWrapper) {
        this.sectionWrapper.style.setProperty('--header-section-height-when-hidden', `${headerSectionHeight}px`);
      }
      // Also set on root for global access
      document.documentElement.style.setProperty('--header-section-height-when-hidden', `${headerSectionHeight}px`);
    } else {
      // Reset when header is visible
      if (this.sectionWrapper) {
        this.sectionWrapper.style.removeProperty('--header-section-height-when-hidden');
      }
      document.documentElement.style.removeProperty('--header-section-height-when-hidden');
    }
  }
  
  /**
   * Handle scroll direction detection (synchronized with main header)
   * Directly checks header's hidden state to ensure perfect synchronization
   */
  handleScrollDirection() {
    const scrollY = window.scrollY;
    const diff = scrollY - this.lastScrollY;
    
    // Skip if menu is open or other conditions that prevent hiding
    if (document.body.classList.contains('mobile-menu-open')) {
      this.lastScrollY = scrollY;
      return;
    }
    
    // Directly sync with header's hidden state using cached reference
    // Ensure header reference is current (it's set in setupStickyNav or setupScrollDirectionHandler)
    if (!this.header) {
      this.header = document.querySelector('.jrs-main-header');
    }
    
    if (this.header) {
      // ALWAYS sync nav with header's hidden state exactly - no exceptions
      // This ensures they hide/show together with no gap
      // This is a backup sync in case MutationObserver missed something
      const headerIsHidden = this.header.classList.contains('header-hidden');
      // Update CSS variable first to ensure transform calculation is correct
      this.updateHeaderHeightVariable(headerIsHidden);
      // Then sync nav state
      this.toggleNavHidden(headerIsHidden);
    } else {
      // Fallback: use same calculation as header if header not found
      if (Math.abs(diff) > this.scrollThreshold) {
        const shouldHide = diff > 0 && scrollY > this.nav.offsetHeight + 20;
        this.toggleNavHidden(shouldHide);
      }
    }
    
    this.lastScrollY = scrollY;
  }
  
  /**
   * Toggle nav hidden state (exactly like main header)
   * Applies hidden class to both nav element and section wrapper
   * Forces sync even if state appears to match to ensure perfect synchronization
   */
  toggleNavHidden(flag) {
    // Force update even if state matches to ensure DOM is in sync
    this.navHidden = flag;
    
    // Directly set/remove class instead of toggle to ensure exact state
    if (flag) {
      this.nav.classList.add('mega-nav--hidden');
    } else {
      this.nav.classList.remove('mega-nav--hidden');
    }
    
    // Also apply to section wrapper to prevent sticky positioning when hidden
    if (this.sectionWrapper) {
      if (flag) {
        this.sectionWrapper.classList.add('mega-nav--hidden');
      } else {
        this.sectionWrapper.classList.remove('mega-nav--hidden');
      }
    }
  }
  
  /**
   * Setup sticky navigation functionality (synchronized with main header)
   * Matches the header pattern: adds class to nav element (like header adds to .jrs-main-header)
   * Also checks header state to ensure nav only sticks when header is sticky
   */
  setupStickyNav() {
    if (this.stickyScrollHandler) return;
    
    // Find the section wrapper for applying hidden class
    this.sectionWrapper = this.nav.closest('.shopify-section-desktop-mega-nav') || 
                         document.querySelector(`#shopify-section-${this.sectionId}`);
    
    // Find header for synchronization (if not already found)
    if (!this.header) {
      this.header = document.querySelector('.jrs-main-header');
    }
    
    // Get header section wrapper for height calculation
    if (!this.headerSection) {
      this.headerSection = this.header?.closest('#shopify-section-jrs-main-header') || 
                          document.querySelector('#shopify-section-jrs-main-header');
    }
    
    let ticking = false;
    
    this.stickyScrollHandler = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const scrollY = window.scrollY || window.pageYOffset;
          const shouldBeSticky = scrollY > this.stickyThreshold;
          
          // Check if header is sticky - nav should only be sticky if header is also sticky
          let headerIsSticky = false;
          if (this.header) {
            headerIsSticky = this.header.classList.contains('jrs-main-header--sticky');
          }
          
          // Nav should be sticky only if:
          // 1. Scrolled past threshold AND
          // 2. Header is also sticky (or header doesn't exist)
          const navShouldBeSticky = shouldBeSticky && (headerIsSticky || !this.header);
          
          if (navShouldBeSticky !== this.isSticky) {
            this.isSticky = navShouldBeSticky;
            
            if (navShouldBeSticky) {
              this.nav.classList.add('mega-nav--sticky');
            } else {
              this.nav.classList.remove('mega-nav--sticky');
            }
          }
          
          ticking = false;
        });
        
        ticking = true;
      }
    };
    
    // Use passive listener for better scroll performance
    window.addEventListener('scroll', this.stickyScrollHandler, { passive: true });
  }
  
  /**
   * Handle mouse entering a nav item
   */
  handleItemEnter(event, item, dropdown) {
    // Clear any pending close
    this.cancelCloseTimeout();
    
    // If different item is active, close it immediately
    if (this.activeItem && this.activeItem !== item) {
      this.closeDropdown(this.activeItem);
    }
    
    // Open this dropdown
    this.openDropdown(item, dropdown);
  }
  
  /**
   * Handle mouse leaving a nav item
   */
  handleItemLeave(event, item, dropdown) {
    const relatedTarget = event.relatedTarget;
    
    // Safety: relatedTarget can be null (mouse left window, etc.)
    if (!relatedTarget) {
      this.startCloseTimeout(item);
      return;
    }
    
    // Check if mouse is going to the dropdown
    if (dropdown && dropdown.contains(relatedTarget)) {
      // Mouse is moving to dropdown - stay open
      return;
    }
    
    // Check if mouse is going to another nav item with dropdown
    const targetItem = relatedTarget.closest('[data-mega-nav-item][data-has-dropdown="true"]');
    if (targetItem && targetItem !== item) {
      // Moving to another dropdown item - that item will handle opening
      // Close this one immediately for smooth transition
      this.closeDropdown(item);
      return;
    }
    
    // Mouse is leaving to somewhere else - start close timer
    this.startCloseTimeout(item);
  }
  
  /**
   * Handle mouse entering a dropdown
   */
  handleDropdownEnter(event, item, dropdown) {
    // Cancel any pending close
    this.cancelCloseTimeout();
  }
  
  /**
   * Handle mouse leaving a dropdown
   */
  handleDropdownLeave(event, item, dropdown) {
    const relatedTarget = event.relatedTarget;
    
    // Safety: relatedTarget can be null
    if (!relatedTarget) {
      this.startCloseTimeout(item);
      return;
    }
    
    // Check if mouse is going back to the parent nav item
    if (item.contains(relatedTarget)) {
      // Going back to nav item - stay open
      return;
    }
    
    // Check if mouse is going to another nav item with dropdown
    const targetItem = relatedTarget.closest('[data-mega-nav-item][data-has-dropdown="true"]');
    if (targetItem && targetItem !== item) {
      // Moving to another dropdown item - close this one immediately
      this.closeDropdown(item);
      return;
    }
    
    // Mouse leaving to somewhere else - start close timer
    this.startCloseTimeout(item);
  }
  
  /**
   * Handle touch start on nav item (for tablets)
   */
  handleTouchStart(event, item, dropdown) {
    // This is handled by click event with preventDefault logic
  }
  
  /**
   * Open a dropdown
   */
  openDropdown(item, dropdown) {
    if (!item || !dropdown) return;
    
    // Set active item
    this.activeItem = item;
    
    // Add active class to item
    item.classList.add('is-active');
    
    // Update ARIA
    const link = item.querySelector('.mega-nav__link');
    if (link) {
      link.setAttribute('aria-expanded', 'true');
    }
    dropdown.setAttribute('aria-hidden', 'false');
    
    // Add class to nav for overlay visibility
    this.nav.classList.add('is-dropdown-open');
    
    // Initialize scroll handler for fade indicator (if scroll-fade is enabled)
    const dropdownInner = dropdown.querySelector('.mega-nav__dropdown-inner');
    if (dropdownInner && dropdown.classList.contains('mega-nav__dropdown--overflow-scroll-fade')) {
      // Cleanup previous scroll handler if exists
      if (this.scrollHandler) {
        this.scrollHandler.destroy();
      }
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        this.scrollHandler = new MegaMenuScroll(dropdown);
      }, 50);
    }
  }
  
  /**
   * Close a dropdown
   */
  closeDropdown(item) {
    if (!item) return;
    
    const dropdown = item.querySelector('[data-mega-nav-dropdown]');
    
    // Reset scroll position before cleanup
    if (dropdown) {
      const dropdownInner = dropdown.querySelector('.mega-nav__dropdown-inner');
      if (dropdownInner) {
        dropdownInner.scrollTop = 0;
      }
    }
    
    // Cleanup scroll handler
    if (this.scrollHandler) {
      this.scrollHandler.destroy();
      this.scrollHandler = null;
    }
    
    // Remove active class
    item.classList.remove('is-active');
    
    // Update ARIA
    const link = item.querySelector('.mega-nav__link');
    if (link) {
      link.setAttribute('aria-expanded', 'false');
    }
    if (dropdown) {
      dropdown.setAttribute('aria-hidden', 'true');
    }
    
    // Clear active item if this was it
    if (this.activeItem === item) {
      this.activeItem = null;
      this.nav.classList.remove('is-dropdown-open');
    }
  }
  
  /**
   * Close all dropdowns
   */
  closeAllDropdowns() {
    this.cancelCloseTimeout();
    
    const activeItems = this.nav.querySelectorAll('.mega-nav__item.is-active');
    activeItems.forEach(item => this.closeDropdown(item));
    
    this.activeItem = null;
    this.nav.classList.remove('is-dropdown-open');
  }
  
  /**
   * Start the close timeout
   */
  startCloseTimeout(item) {
    this.cancelCloseTimeout();
    
    this.closeTimeout = setTimeout(() => {
      this.closeDropdown(item);
    }, this.CLOSE_DELAY);
  }
  
  /**
   * Cancel any pending close timeout
   */
  cancelCloseTimeout() {
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = null;
    }
  }
  
  /**
   * Handle overlay click
   */
  handleOverlayClick(event) {
    this.closeAllDropdowns();
  }
  
  /**
   * Handle keyboard events
   */
  handleKeyDown(event) {
    if (event.key === 'Escape' && this.activeItem) {
      this.closeAllDropdowns();
      
      // Return focus to the nav link
      const link = this.activeItem?.querySelector('.mega-nav__link');
      if (link) {
        link.focus();
      }
    }
  }
  
  /**
   * Handle window resize
   */
  handleResize() {
    if (window.innerWidth < this.MOBILE_BREAKPOINT) {
      this.closeAllDropdowns();
    }
    
    // Recalculate header height if header is currently hidden (in case dimensions changed)
    if (this.header && this.header.classList.contains('header-hidden')) {
      this.updateHeaderHeightVariable(true);
    }
  }
  
  /**
   * Debounce utility function
   */
  debounce(func, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }
  
  /**
   * Cleanup when section is unloaded
   */
  destroy() {
    this.closeAllDropdowns();
    
    // Cleanup scroll handler
    if (this.scrollHandler) {
      this.scrollHandler.destroy();
      this.scrollHandler = null;
    }
    
    document.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('resize', this.debouncedResize);
    
    if (this.stickyScrollHandler) {
      window.removeEventListener('scroll', this.stickyScrollHandler);
      this.stickyScrollHandler = null;
    }
    
    if (this.scrollDirectionListener) {
      window.removeEventListener('scroll', this.scrollDirectionListener);
      this.scrollDirectionListener = null;
    }
    
    if (this.headerObserver) {
      this.headerObserver.disconnect();
      this.headerObserver = null;
    }
    
    if (this.overlay) {
      this.overlay.removeEventListener('click', this.handleOverlayClick);
    }
  }
}

/**
 * Mega Menu Scroll Handler
 * Detects if content is scrollable and tracks scroll position for fade indicator
 */
class MegaMenuScroll {
  constructor(dropdownElement) {
    this.dropdown = dropdownElement;
    this.content = dropdownElement.querySelector('.mega-nav__dropdown-inner');
    
    if (!this.content) return;
    
    this.init();
  }
  
  init() {
    this.checkScrollable();
    this.bindEvents();
  }
  
  checkScrollable() {
    const isScrollable = this.content.scrollHeight > this.content.clientHeight;
    this.content.classList.toggle('is-scrollable', isScrollable);
    
    if (isScrollable) {
      this.checkScrollPosition();
    }
  }
  
  checkScrollPosition() {
    const { scrollTop, scrollHeight, clientHeight } = this.content;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;
    const isAtTop = scrollTop <= 10;
    
    this.content.classList.toggle('is-scrolled-bottom', isAtBottom);
    this.content.classList.toggle('is-scrolled-top', isAtTop);
  }
  
  bindEvents() {
    // Check on scroll
    this.content.addEventListener('scroll', () => {
      this.checkScrollPosition();
    }, { passive: true });
    
    // Recheck on window resize
    window.addEventListener('resize', () => {
      this.checkScrollable();
    }, { passive: true });
    
    // Recheck when dropdown opens (for dynamic content)
    const observer = new MutationObserver(() => {
      this.checkScrollable();
    });
    
    observer.observe(this.content, { 
      childList: true, 
      subtree: true 
    });
    
    this.observer = observer;
  }
  
  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}

/**
 * Initialize mega nav on DOM ready
 */
function initMegaNav() {
  const megaNavs = document.querySelectorAll('[data-mega-nav]');
  
  megaNavs.forEach(nav => {
    // Check if already initialized
    if (nav.megaNavController) return;
    
    // Initialize controller
    nav.megaNavController = new MegaNavController(nav);
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initMegaNav);
} else {
  initMegaNav();
}

// Re-initialize on Shopify section load (theme editor)
document.addEventListener('shopify:section:load', (event) => {
  const section = event.target;
  const megaNav = section.querySelector('[data-mega-nav]');
  
  if (megaNav && !megaNav.megaNavController) {
    megaNav.megaNavController = new MegaNavController(megaNav);
  }
});

